/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, XCircle, Guitar, RefreshCw, Target } from 'lucide-react';
import { FretPosition } from '../Fretboard';
import { QuizScore } from './FretboardQuiz';

interface ChordRecognitionQuizProps {
  tuning: string[];
  fretRange: { start: number; end: number };
  userFretboardAnswers: FretPosition[];
  setUserFretboardAnswers: (answers: FretPosition[]) => void;
  setHighlightedFretPositions: (positions: FretPosition[]) => void;
  setRootNotePosition: (position: FretPosition | null) => void;
  updateScore: (correct: number, total: number, streak: number) => void;
  currentScore: QuizScore;
  currentKey: string;
}

interface ChordQuestion {
  chordName: string;
  rootNote: string;
  chordType: string;
  targetString?: number; // For "root on X string" questions
  questionType: 'basicShape' | 'rootOnString' | 'identifyChord';
  correctPositions: FretPosition[];
  allowedVariations: FretPosition[][]; // Multiple valid fingerings
}

// Correct chord shapes for standard tuning (EADGBE)
// String numbering: 6=low E, 5=A, 4=D, 3=G, 2=B, 1=high E
const basicChordShapes: { [key: string]: { [root: string]: FretPosition[] } } = {
  'major': {
    'C': [ // Standard C major chord
      { string: 6, fret: -1, note: '' },  // Don't play low E
      { string: 5, fret: 3, note: 'C' },  // 3rd fret A string (root)
      { string: 4, fret: 2, note: 'E' },  // 2nd fret D string
      { string: 3, fret: 0, note: 'G' },  // Open G string
      { string: 2, fret: 1, note: 'C' },  // 1st fret B string
      { string: 1, fret: 0, note: 'E' },  // Open high E string
    ],
    'G': [ // Standard G major chord
      { string: 6, fret: 3, note: 'G' },  // 3rd fret low E
      { string: 5, fret: 2, note: 'B' },  // 2nd fret A string
      { string: 4, fret: 0, note: 'D' },  // Open D string
      { string: 3, fret: 0, note: 'G' },  // Open G string (root)
      { string: 2, fret: 0, note: 'B' },  // Open B string
      { string: 1, fret: 3, note: 'G' },  // 3rd fret high E
    ],
    'D': [ // Standard D major chord
      { string: 6, fret: -1, note: '' },  // Don't play low E
      { string: 5, fret: -1, note: '' },  // Don't play A string
      { string: 4, fret: 0, note: 'D' },  // Open D string (root)
      { string: 3, fret: 2, note: 'A' },  // 2nd fret G string
      { string: 2, fret: 3, note: 'D' },  // 3rd fret B string
      { string: 1, fret: 2, note: 'F#' }, // 2nd fret high E
    ]
  }
};

// Barre chord shapes for "root on string" questions
// String numbering: 6=low E, 5=A, 4=D, 3=G, 2=B, 1=high E
const barreChordShapes = {
  'C': {
    // C major barre chord with root on low E string (6th string) - 8th fret
    6: [
      { string: 6, fret: 8, note: 'C' },  // 8th fret low E (root)
      { string: 5, fret: 10, note: 'G' }, // 10th fret A
      { string: 4, fret: 10, note: 'C' }, // 10th fret D
      { string: 3, fret: 9, note: 'E' },  // 9th fret G
      { string: 2, fret: 8, note: 'G' },  // 8th fret B
      { string: 1, fret: 8, note: 'C' },  // 8th fret high E
    ],
    // C major with root on A string (5th string) - standard C shape
    5: [
      { string: 6, fret: -1, note: '' },  // Don't play low E
      { string: 5, fret: 3, note: 'C' },  // 3rd fret A (root)
      { string: 4, fret: 2, note: 'E' },  // 2nd fret D
      { string: 3, fret: 0, note: 'G' },  // Open G
      { string: 2, fret: 1, note: 'C' },  // 1st fret B
      { string: 1, fret: 0, note: 'E' },  // Open high E
    ],
    // C major with root on D string (4th string) - 10th fret barre shape
    4: [
      { string: 6, fret: -1, note: '' },  // Don't play low E
      { string: 5, fret: -1, note: '' },  // Don't play A
      { string: 4, fret: 10, note: 'C' }, // 10th fret D (root)
      { string: 3, fret: 9, note: 'E' },  // 9th fret G
      { string: 2, fret: 8, note: 'G' },  // 8th fret B
      { string: 1, fret: 8, note: 'C' },  // 8th fret high E
    ]
  }
};

const ChordRecognitionQuiz: React.FC<ChordRecognitionQuizProps> = ({
  tuning,
  fretRange,
  userFretboardAnswers,
  setUserFretboardAnswers,
  setHighlightedFretPositions,
  setRootNotePosition,
  updateScore,
  currentScore,
  currentKey
}) => {
  const [currentQuestion, setCurrentQuestion] = useState<ChordQuestion | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);

  // Available chords for quiz (basic chords for now)
  const availableChords = [
    { name: 'C major', root: 'C', type: 'major' },
    { name: 'G major', root: 'G', type: 'major' },
    { name: 'D major', root: 'D', type: 'major' },
    { name: 'Em', root: 'E', type: 'minor' },
    { name: 'Am', root: 'A', type: 'minor' }
  ];

  // Generate a new question
  const generateNewQuestion = () => {
    const questionTypes: ChordQuestion['questionType'][] = ['basicShape', 'rootOnString'];
    const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
    const randomChord = availableChords[Math.floor(Math.random() * availableChords.length)];
    
    let newQuestion: ChordQuestion;

    switch (randomType) {
      case 'basicShape':
        // "Draw a G major chord"
        const basicShape = basicChordShapes[randomChord.type]?.[randomChord.root];
        newQuestion = {
          chordName: randomChord.name,
          rootNote: randomChord.root,
          chordType: randomChord.type,
          questionType: 'basicShape',
          correctPositions: basicShape ? basicShape.filter(pos => pos.fret >= 0) : [],
          allowedVariations: basicShape ? [basicShape.filter(pos => pos.fret >= 0)] : []
        };
        break;

      case 'rootOnString':
        // Use barre chord shapes for root-on-string questions
        const targetString = Math.floor(Math.random() * 3) + 4; // Strings 4, 5, or 6 (bass strings)
        
        // For now, only support C major chord for simplicity
        const rootOnStringShape = barreChordShapes['C']?.[targetString];
        
        newQuestion = {
          chordName: 'C major',
          rootNote: 'C',
          chordType: 'major',
          targetString: targetString,
          questionType: 'rootOnString',
          correctPositions: rootOnStringShape ? rootOnStringShape.filter(pos => pos.fret >= 0) : [],
          allowedVariations: rootOnStringShape ? [rootOnStringShape.filter(pos => pos.fret >= 0)] : []
        };
        break;

      default:
        // Fallback to basic shape
        const fallbackShape = basicChordShapes['major']['C'];
        newQuestion = {
          chordName: 'C major',
          rootNote: 'C',
          chordType: 'major',
          questionType: 'basicShape',
          correctPositions: fallbackShape ? fallbackShape.filter(pos => pos.fret >= 0) : [],
          allowedVariations: fallbackShape ? [fallbackShape.filter(pos => pos.fret >= 0)] : []
        };
    }

    setCurrentQuestion(newQuestion);
    setShowAnswer(false);
    setFeedback(null);
    setUserFretboardAnswers([]);
    setHighlightedFretPositions([]);
    setRootNotePosition(null);
  };

  // Check if user's answer matches any allowed variation
  const checkAnswer = () => {
    if (!currentQuestion || userFretboardAnswers.length === 0) return;

    // For now, use a simple matching algorithm
    // In a real implementation, you'd want more sophisticated chord recognition
    let isCorrect = false;

    for (const variation of currentQuestion.allowedVariations) {
      const matchedPositions = userFretboardAnswers.filter(userPos =>
        variation.some(correctPos =>
          correctPos.string === userPos.string && 
          correctPos.fret === userPos.fret
        )
      );

      // Consider correct if user got at least 70% of the chord positions right
      const accuracy = matchedPositions.length / variation.length;
      if (accuracy >= 0.7) {
        isCorrect = true;
        break;
      }
    }

    setFeedback(isCorrect ? 'correct' : 'incorrect');
    setShowAnswer(true);
    setHighlightedFretPositions(currentQuestion.correctPositions);

    // Update score
    const newTotal = currentScore.total + 1;
    const newCorrect = currentScore.correct + (isCorrect ? 1 : 0);
    const newStreak = isCorrect ? currentScore.streak + 1 : 0;
    updateScore(newCorrect, newTotal, newStreak);
  };

  // Next question
  const nextQuestion = () => {
    generateNewQuestion();
  };

  // Generate first question on mount
  useEffect(() => {
    generateNewQuestion();
  }, []);

  // Get question text
  const getQuestionText = (): string => {
    if (!currentQuestion) return '';

    switch (currentQuestion.questionType) {
      case 'basicShape':
        return `Draw a ${currentQuestion.chordName} chord`;
      case 'rootOnString':
        const stringNames = ['', 'high E', 'B', 'G', 'D', 'A', 'low E'];
        const stringName = stringNames[currentQuestion.targetString!] || 'target';
        return `Draw a ${currentQuestion.chordName} chord with root on the ${stringName} string`;
      default:
        return '';
    }
  };

  if (!currentQuestion) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p>Loading question...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Question Display */}
      <Card className="p-4">
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <Guitar className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold">{getQuestionText()}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Click on the fretboard to place your fingers for this chord
          </p>
        </div>
      </Card>

      {/* User Progress */}
      {userFretboardAnswers.length > 0 && !showAnswer && (
        <Card className="p-3 bg-yellow-50 border-yellow-200">
          <div className="text-center">
            <p className="text-sm text-yellow-800">
              You've placed {userFretboardAnswers.length} finger{userFretboardAnswers.length !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              {userFretboardAnswers.map((pos, i) => (
                <span key={i} className="inline-block bg-yellow-200 px-1 rounded mr-1">
                  String {pos.string}, Fret {pos.fret}
                </span>
              ))}
            </p>
            <div className="flex justify-center gap-2 mt-2">
              <Button size="sm" onClick={checkAnswer}>
                Submit Chord
              </Button>
              <Button size="sm" variant="outline" onClick={() => setUserFretboardAnswers([])}>
                Clear All
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Answer Feedback */}
      {showAnswer && (
        <Card className={`p-4 ${
          feedback === 'correct' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2">
              {feedback === 'correct' ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600" />
              )}
              <span className={`font-semibold ${
                feedback === 'correct' ? 'text-green-800' : 'text-red-800'
              }`}>
                {feedback === 'correct' ? 'Great job!' : 'Keep practicing!'}
              </span>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm">
                {feedback === 'correct' 
                  ? `You correctly played the ${currentQuestion.chordName} chord!`
                  : `Here's the correct fingering for ${currentQuestion.chordName}:`
                }
              </p>
              <div className="text-xs text-muted-foreground">
                The correct chord shape is highlighted on the fretboard
              </div>
            </div>

            <Button onClick={nextQuestion} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Next Chord
            </Button>
          </div>
        </Card>
      )}

      {/* Quick Start (when no fingers placed yet) */}
      {userFretboardAnswers.length === 0 && !showAnswer && (
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="text-center space-y-2">
            <p className="text-sm text-green-800 font-medium">Start placing your fingers!</p>
            <p className="text-xs text-green-600">
              Click on multiple fret positions to form the chord shape. 
              You can click the same position again to remove a finger.
            </p>
          </div>
        </Card>
      )}

      {/* Chord Learning Tip */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="space-y-2">
          <h5 className="text-sm font-medium text-blue-900">💡 Chord Tip</h5>
          <p className="text-xs text-blue-800">
            {currentQuestion.chordType === 'major' && 
              "Major chords have a bright, happy sound. They contain the root, major third, and perfect fifth."}
            {currentQuestion.chordType === 'minor' && 
              "Minor chords have a more melancholic sound. They contain the root, minor third, and perfect fifth."}
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ChordRecognitionQuiz;